/**
 * engine/speed.ts — the element's speed while a finger is on the record.
 *
 * Task 09. `spec.md` «Пластинка». The finger's arithmetic is `lib/platter.ts`; this module is
 * the only one that writes `playbackRate` and `preservesPitch`, and it owns the rules about
 * how those two are written.
 *
 * ## The pitch follows the speed off 1,0
 *
 * With `preservesPitch` on, a browser stretches the sound to keep its tone — a record slowed
 * by hand would sound like a slowed podcast. So the pitch is let go at every speed but 1,0,
 * and kept again at 1,0 (`spec.md` «Пластинка»). The order matters at the two edges: off
 * **before** the speed leaves 1,0, on **after** it is back, so no moment plays a changed speed
 * at a kept tone. Older WebKit knows the switch only as `webkitPreservesPitch`; both are
 * written where both exist.
 *
 * ## One write per 50 ms, and the last one always lands
 *
 * A phone reports a moving finger 60–120 times a second. The old project wrote the element on
 * every one of them, and on a phone the sound died under the finger: each write restarts the
 * element's own machinery before the last one has produced anything (`spec.md` «Пластинка»:
 * «это „шторм“, после которого звук пропадает»). So a speed is written at most once per
 * {@link WRITE_EVERY_MS}. A request that comes sooner is not dropped — it waits for the 50 ms
 * to run out, and whatever the finger asked for last is what is written then. A request equal
 * to the speed the element already has writes nothing and costs nothing.
 *
 * ## The return is a slope, not a step
 *
 * A released record comes back to 1,0 in {@link RETURN_STEPS} even steps over
 * {@link RETURN_MS} — a motor taking the platter back up to speed. A jump from 0 to 1,0 is a
 * click in the speaker, and a jump from 2,0 is a lurch. A new touch during the slope stops it
 * where it is. From a standstill the slope starts at the floor, 0,55, not at a sixth of 1,0:
 * WebKit renders the speeds below it as silence, and a slope through them was 150 ms of
 * nothing and then a jump — the sound comes back low and rises instead.
 *
 * ## A held record is speed 0, never a pause
 *
 * `playbackRate = 0` stops the sound and leaves the element playing: no `pause` event, no
 * change of the button, of the lock screen or of the slice. A pause would flip all three for
 * the length of a touch, and undoing it needs a `play()` — a promise that can be refused.
 * The one thing that does notice the silence is the watchdog, which would call twelve seconds
 * of a held record «звук не идёт»; `engine/player.ts` asks {@link SpeedControl.touched}.
 *
 * ## A hand that gives the sound back at once (task 18)
 *
 * The scratch voice (`engine/scratch.ts`) holds the element at 0 for the whole touch and the
 * handover after it, and then the element must sound at once, at full speed, exactly where the
 * voice fades out: a slope there would be 0,3 s of a slowed record under a voice already gone.
 * {@link SpeedControl.releaseNow} is that: the touch ends and the speed is written in one step —
 * still no sooner than 50 ms after the write before, like every other write.
 *
 * ## Back to the tempo, not to 1,0 (task 23)
 *
 * The deck's pitch slider sets the speed of a record nobody touches: `1 + tempo`, from 0,92 to
 * 1,08 ({@link SpeedControl.setBase}). Every way the sound comes back after a hand — the slope of
 * task 09, the scratch's handover, the needle put down — comes back to that, not to
 * {@link NORMAL_RATE}. A slider being dragged asks for a new base on every move of the finger,
 * so the base goes through the same one-write-per-50-ms as the finger. The pitch rule does not
 * change: the tone is kept at exactly 1 and let go at every other speed, a tempo of +3 % too —
 * a record played faster sounds higher, as on a deck.
 *
 * ## KEYLOCK (task 55)
 *
 * {@link SpeedControl.setKeylock} keeps the tone at every speed of the deck's own — its tempo, a
 * PITCH BEND, a mix — as the pult's KEYLOCK does (`docs/superpowers/specs/2026-10-05-pult-design.md`,
 * «Две равные деки»). Not under a hand: a record moved by a finger is a record, and a scratch with
 * its tone held would be a stretched sound, not a scratch. The slope back after the hand keeps the
 * tone already — the hand is gone. The probe of task 53 found the switch on the owner's iPhone;
 * how a held tone sounds at ±8 % the owner could not tell by ear.
 */

import { MIN_RATE } from '../lib/platter';

import type { MediaElementLike } from './element';

/** The speed of a record nobody touches. */
export const NORMAL_RATE = 1;

/** At most one write of the speed per this many milliseconds. */
export const WRITE_EVERY_MS = 50;

/** A released record is back at {@link NORMAL_RATE} this long after the finger left. */
export const RETURN_MS = 300;

/** …in this many even steps: one every 50 ms, the same pace as every other write. */
export const RETURN_STEPS = 6;

export interface SpeedControl {
  /** A finger landed on the record. The speed is left as it is until {@link set}. */
  touch(): void;
  /**
   * A hand silences the record at once — the scratch taking the sound, the needle lifted (tasks
   * 18, 22): a touch, and speed 0, within the 50 ms rule.
   */
  hush(): void;
  /** The speed the finger asks for: `0` or a speed the platter allows. */
  set(rate: number): void;
  /** The finger left the record: back to the {@link SpeedControl.base}, by the slope. */
  release(): void;
  /** The touch ends and the speed is the {@link SpeedControl.base} in one step, without the slope. */
  releaseNow(): void;
  /**
   * Task 23: the speed of a record nobody touches — `1 + tempo`. Written now (within the 50 ms
   * rule) when no hand is on the record; kept for the return when one is.
   */
  setBase(rate: number): void;
  /** Task 55: KEYLOCK — the tone held at every speed but a hand's. */
  setKeylock(on: boolean): void;
  /** The speed of a record nobody touches: {@link NORMAL_RATE} until a tempo is set. */
  readonly base: number;
  /** A finger is on the record now. */
  readonly touched: boolean;
  dispose(): void;
}

/**
 * `now` is a monotonic clock in milliseconds. Not the player's: that one is the wall clock
 * the sound links' expiry is compared with, and it counts whole milliseconds — two writes it
 * calls 50 ms apart can be 49.2 apart in fact, which a real browser showed
 * (`.omc/wiki/raw/zadacha-09-proverka.md`).
 */
export function createSpeed(
  el: MediaElementLike,
  now: () => number = () => performance.now(),
): SpeedControl {
  let touched = false;
  let slope: ReturnType<typeof setTimeout> | undefined;
  /** The speed of a record nobody touches: the tempo's (task 23). */
  let base = NORMAL_RATE;
  /** What was asked for last: the finger's speed, or the base on its way back. */
  let wanted = NORMAL_RATE;
  let lastWriteAt = Number.NEGATIVE_INFINITY;
  /** The write of {@link wanted} waiting for its 50 ms. */
  let pending: ReturnType<typeof setTimeout> | undefined;
  /** Task 55: KEYLOCK on. */
  let keylock = false;

  function stopPending(): void {
    if (pending !== undefined) clearTimeout(pending);
    pending = undefined;
  }

  function stopSlope(): void {
    if (slope !== undefined) clearTimeout(slope);
    slope = undefined;
  }

  function keepPitch(keep: boolean): void {
    if (el.preservesPitch !== keep) el.preservesPitch = keep;
    if ('webkitPreservesPitch' in el && el.webkitPreservesPitch !== keep) {
      el.webkitPreservesPitch = keep;
    }
  }

  /** The tone is kept at `rate`: at exactly 1, and — KEYLOCK on, no hand on the record — at any. */
  function keepsAt(rate: number): boolean {
    return rate === NORMAL_RATE || (keylock && !touched);
  }

  function write(rate: number): void {
    const keep = keepsAt(rate);
    if (!keep) keepPitch(false);
    if (el.playbackRate !== rate) {
      el.playbackRate = rate;
      lastWriteAt = now();
    }
    if (keep) keepPitch(true);
  }

  /**
   * {@link wanted} now, or as soon as the last write is 50 ms old. A timer that wakes asks the
   * clock again rather than writing blindly: Chromium fires a timer a fraction of a
   * millisecond before its own `performance.now()` says the time is up, and a blind write
   * landed 49.3 ms after the one before (`.omc/wiki/raw/zadacha-09-proverka.md`).
   */
  function flush(): void {
    if (pending !== undefined) return;
    const wait = lastWriteAt + WRITE_EVERY_MS - now();
    if (wait <= 0) {
      write(wanted);
      return;
    }
    pending = setTimeout(() => {
      pending = undefined;
      // Every way out of a touch that must not write — a lift, a new finger — stops this timer.
      flush();
    }, wait);
  }

  /**
   * From the speed the element has now back to the base, one step every 50 ms — each step,
   * like {@link flush}, asking the clock first, so the slope keeps to the same
   * one-write-per-50-ms as the finger.
   */
  function slopeBack(): void {
    stopSlope();
    const to = base;
    const from = Number.isFinite(el.playbackRate) ? el.playbackRate : to;
    if (from === to) {
      if (keepsAt(to)) keepPitch(true);
      return;
    }
    // The last step is the exact base, not `from` plus a sum that may land a hair off it.
    const steps = Array.from({ length: RETURN_STEPS }, (_, i) => {
      if (i === RETURN_STEPS - 1) return to;
      if (from < MIN_RATE) return MIN_RATE + ((to - MIN_RATE) * i) / (RETURN_STEPS - 1);
      return from + ((to - from) * (i + 1)) / RETURN_STEPS;
    });
    let step = 0;
    const next = (): void => {
      const early = lastWriteAt + WRITE_EVERY_MS - now();
      if (early > 0) {
        slope = setTimeout(next, early);
        return;
      }
      write(steps[step] ?? to);
      step += 1;
      slope = step < RETURN_STEPS ? setTimeout(next, RETURN_MS / RETURN_STEPS) : undefined;
    };
    slope = setTimeout(next, RETURN_MS / RETURN_STEPS);
  }

  return {
    touch(): void {
      touched = true;
      stopSlope();
      // A handback still waiting for its 50 ms is overtaken: the new hand decides the speed.
      stopPending();
    },
    set(rate: number): void {
      if (!touched) return;
      wanted = rate;
      flush();
    },
    hush(): void {
      this.touch();
      this.set(0);
    },
    release(): void {
      if (!touched) return;
      touched = false;
      stopPending();
      slopeBack();
    },
    releaseNow(): void {
      if (!touched) return;
      touched = false;
      stopPending();
      stopSlope();
      // A finger that lands again within the 50 ms stops this write (`touch`).
      wanted = base;
      flush();
    },
    setBase(rate: number): void {
      if (!Number.isFinite(rate) || rate <= 0) return;
      base = rate;
      // Under a hand the base waits for the return; the hand has the speed now.
      if (touched) return;
      stopSlope();
      wanted = base;
      flush();
    },
    setKeylock(on: boolean): void {
      if (keylock === on) return;
      keylock = on;
      // Under a hand the switch waits for the hand to go; the tone follows the speed there.
      if (!touched) keepPitch(keepsAt(el.playbackRate));
    },
    get base(): number {
      return base;
    },
    get touched(): boolean {
      return touched;
    },
    dispose(): void {
      stopPending();
      stopSlope();
      touched = false;
      if (el.playbackRate !== NORMAL_RATE) write(NORMAL_RATE);
    },
  };
}
